import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { AuthRole } from '../../../features/auth/models/auth-role.enum';
import { CurrentUser } from '../../../features/auth/models/current-user.model';
import { LoginResponse } from '../../../features/auth/models/login-response.model';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly accessTokenSignal = signal<string | null>(this.tokenStorage.getAccessToken());
  private readonly currentUserSignal = signal<CurrentUser | null>(
    this.tokenStorage.getCurrentUser(),
  );

  public readonly currentUser = this.currentUserSignal.asReadonly();
  public readonly isAuthenticated = computed(() =>
    Boolean(this.accessTokenSignal() && this.currentUserSignal()),
  );

  // Inicia a sessão apos login e mantem token/usuario persistidos em um unico fluxo.
  public startSession(response: LoginResponse): void {
    this.tokenStorage.saveAccessToken(response.accessToken);
    this.tokenStorage.saveCurrentUser(response.user);
    this.accessTokenSignal.set(response.accessToken);
    this.currentUserSignal.set(response.user);
  }

  // Encerra a sessão local e, quando solicitado, leva o usuario de volta ao login.
  public endSession(redirectToLogin = true): void {
    this.tokenStorage.clear();
    this.accessTokenSignal.set(null);
    this.currentUserSignal.set(null);

    if (redirectToLogin) {
      void this.router.navigateByUrl('/login');
    }
  }

  public restoreSession(): void {
    this.accessTokenSignal.set(this.tokenStorage.getAccessToken());
    this.currentUserSignal.set(this.tokenStorage.getCurrentUser());
  }

  public hasRole(role: AuthRole): boolean {
    return this.normalizeRole(this.currentUserSignal()?.role) === role;
  }

  public hasAnyRole(roles: AuthRole[]): boolean {
    const currentRole = this.normalizeRole(this.currentUserSignal()?.role);

    return Boolean(currentRole && roles.includes(currentRole));
  }

  public getHomeRouteForCurrentUser(): string {
    const role = this.currentUserSignal()?.role;

    return this.getHomeRouteByRole(role);
  }

  public redirectToHomeByRole(): Promise<boolean> {
    return this.router.navigateByUrl(this.getHomeRouteForCurrentUser(), { replaceUrl: true });
  }

  public setUser(user: CurrentUser) {
    this.tokenStorage.saveCurrentUser(user);
    this.currentUserSignal.set(user);
  }

  public updateUser(user: CurrentUser) {
    this.tokenStorage.saveCurrentUser(user);
    this.currentUserSignal.set(user);
  }

  public patchUser(data: Partial<CurrentUser>) {
    const currentUserSignal = this.currentUserSignal();

    if (!currentUserSignal) return;

    const updatedUser = {
      ...currentUserSignal,
      ...data,
    };

    this.tokenStorage.saveCurrentUser(updatedUser);
    this.currentUserSignal.set(updatedUser);
  }

  public clearUser() {
    this.currentUserSignal.set(null);
  }

  // Mantem a regra de destino inicial por perfil centralizada para guards e paginas.
  private getHomeRouteByRole(role: AuthRole | string | undefined): string {
    switch (this.normalizeRole(role)) {
      case AuthRole.Admin:
      case AuthRole.Senatran:
        return '/admin/dashboard';
      case AuthRole.Company:
        return '/solicitacoes';
      // return '/company/dashboard';
      case AuthRole.Citizen:
        return '/solicitacoes';
      default:
        return '/login';
    }
  }

  private normalizeRole(role: AuthRole | string | undefined): AuthRole | undefined {
    // A API documentada retorna "Admin"; esta normalizacão evita quebra por variação de caixa.
    const normalizedRole = role?.trim().toLowerCase();

    switch (normalizedRole) {
      case 'admin':
        return AuthRole.Admin;
      case 'senatran':
        return AuthRole.Senatran;
      case 'company':
        return AuthRole.Company;
      case 'citizen':
        return AuthRole.Citizen;
      default:
        return undefined;
    }
  }
}
